import { CATEGORIES, type Dimension, dec, plural, ZERO } from "@bl/domain";
import { useNavigate } from "@tanstack/react-router";
import { MapPin, SlidersHorizontal, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import {
  type Invoice,
  type InvoiceLine,
  type Location,
  send,
  useChange,
  useItems,
  useLocations,
} from "../api";
import { catStyle, ErrorNote } from "../components/ui";
import { categoryLabel } from "../format";
import { useNotify } from "../notify";
import { GivePack, METHODS, money } from "./InvoiceDocumentStep";
import type { LineView } from "./InvoiceLotsStep";

const MEASURED_BY: Record<Dimension, string> = {
  mass: "weight",
  volume: "volume",
  count: "count",
  pitch: "pitches",
};
/**
 * Why a line cannot be received yet, where its pack size is missing: receiving counts a line in
 * packs, and neither the document nor the item says how much is in one. CMG's "1GAL" against a
 * fining counted by weight was found only when Receive was pressed (Tyler, 2026-10-01).
 */
function packProblem(line: InvoiceLine, dimension: Dimension | undefined): string {
  if (line.printedPack && line.soldBy && dimension && line.soldBy !== dimension) {
    return `printed as ${line.printedPack}, but the item is counted by ${MEASURED_BY[dimension]}.`;
  }
  return line.printedPack
    ? `printed as ${line.printedPack}, which is not a size, and the item has none.`
    : "the invoice prints no size and the item has none.";
}
/** Everything without a default location of its own. */
const EVERYTHING_ELSE = "*";
/**
 * The kinds of stock on an invoice, each with where it goes by default (Tyler, 2026-10-01): a kind a
 * location is the default for gets a place of its own, and the rest share everything else's. An
 * invoice of grain alone is one choice; grain and hops are two.
 */
function kindsOf(lines: InvoiceLine[], open: readonly Location[]) {
  const defaults = new Map<string, string>();
  for (const l of open) for (const kind of l.defaultFor) defaults.set(kind, l.id);
  const kindOf = (line: InvoiceLine) =>
    line.itemCategory && defaults.has(line.itemCategory) ? line.itemCategory : EVERYTHING_ELSE;
  const order: string[] = [...CATEGORIES, EVERYTHING_ELSE];
  const kinds = [...new Set(lines.map(kindOf))].sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const groups = kinds.map((kind) => {
    const of = lines.filter((l) => kindOf(l) === kind);
    // A share of everything else that is all one category is named and colored for it.
    const categories = [...new Set(of.map((l) => l.itemCategory))];
    const category =
      kind !== EVERYTHING_ELSE ? kind : categories.length === 1 ? (categories[0] ?? null) : null;
    return {
      kind,
      lines: of,
      category,
      label: category
        ? categoryLabel(category)
        : kinds.length === 1
          ? "Everything"
          : "Everything else",
      usual: defaults.get(kind) ?? "",
    };
  });
  return { groups, kindOf };
}
/** A location to choose, in soft red until one is. */
function PlaceSelect({
  label,
  value,
  open,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  open: readonly Location[];
  disabled?: boolean;
  onChange: (locationId: string) => void;
}) {
  return (
    <select
      className={`select${value ? "" : " needs-place"}`}
      aria-label={label}
      aria-invalid={value ? undefined : true}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {value ? null : <option value="">Choose a location</option>}
      {open.map((l) => (
        <option key={l.id} value={l.id}>
          {l.name}
        </option>
      ))}
    </select>
  );
}
/**
 * The last pass. Each kind of stock on the invoice goes to its own place, starting at the location
 * that is its default, so a delivery of grain and hops is two choices already made (Tyler,
 * 2026-10-01). "Customize location per item" gives every line a location of its own, starting where
 * its kind was going (Tyler, 2026-09-28). Each line's choice is stored on the line, so it survives a
 * reload or another device. Going back to one place per kind clears them, so the kinds' places are
 * the only ones.
 */
export function ReceiveStep({
  invoice,
  savedKinds,
  onKinds,
  savedSplit,
  onSplit,
}: {
  invoice: Invoice;
  savedKinds?: Record<string, string>;
  onKinds: (kindLocations: Record<string, string>) => void;
  savedSplit?: boolean;
  onSplit: (split: boolean) => void;
}) {
  const { data: locations = [] } = useLocations();
  const open = useMemo(() => locations.filter((l) => !l.archived), [locations]);
  const navigate = useNavigate();
  const notify = useNotify();

  const stockLines = invoice.lines.filter((l) => l.receivesStock);
  const unmatched = stockLines.filter((l) => !l.itemId);
  const unpacked = stockLines.filter((l) => l.needsPack);
  const { data: items = [] } = useItems();
  const dimensionOf = (itemId: string | null) =>
    items.find((i) => i.id === itemId)?.dimension as Dimension | undefined;
  const lots = stockLines.reduce((n, l) => n + Math.max(l.lots.length, 1), 0);
  const { groups, kindOf } = kindsOf(stockLines, open);

  // A kind's place as chosen here, else its default. A location archived since it was chosen is no
  // choice at all.
  const [picked, setPicked] = useState<Record<string, string>>(savedKinds ?? {});
  const isOpen = (id: string) => open.some((l) => l.id === id);
  const choiceOf = (kind: string) => {
    const mine = picked[kind];
    if (mine !== undefined && (mine === "" || isOpen(mine))) return mine;
    return groups.find((g) => g.kind === kind)?.usual ?? "";
  };
  const choose = (kind: string, locationId: string) => {
    const next = { ...picked, [kind]: locationId };
    setPicked(next);
    onKinds(next);
  };

  // Each line's place as last chosen here, ahead of the server confirming it, so a select does not
  // snap back while the change is on its way. One change at a time: a second sent before the first
  // is answered would go under the same idempotency key and be refused. If a change fails, the
  // screen goes back to what the server holds, so it never shows a place receiving will not use.
  const place = useChange(send.patchInvoice);
  const [placed, setPlaced] = useState<Record<string, string | null>>({});
  const placeOf = (line: LineView) =>
    !place.isError && line.id in placed ? (placed[line.id] ?? null) : line.locationId;
  const [custom, setCustom] = useState(savedSplit ?? stockLines.some((l) => l.locationId !== null));
  const putLines = (lines: { id: string; locationId: string | null }[]) => {
    if (lines.length === 0) return;
    setPlaced((all) => ({ ...all, ...Object.fromEntries(lines.map((l) => [l.id, l.locationId])) }));
    place.mutate({ id: invoice.id, patch: { lines } });
  };
  const turnCustom = (on: boolean) => {
    setCustom(on);
    onSplit(on);
    if (on) {
      putLines(
        stockLines
          .filter((l) => !placeOf(l))
          .map((l) => ({ id: l.id, locationId: choiceOf(kindOf(l)) || null }))
          .filter((l) => l.locationId !== null),
      );
    } else {
      putLines(stockLines.filter((l) => placeOf(l)).map((l) => ({ id: l.id, locationId: null })));
    }
  };
  const placeChosen = custom
    ? stockLines.every((l) => placeOf(l))
    : groups.every((g) => choiceOf(g.kind));

  // Receiving puts each kind's place on its lines first, so kinds land in different places. An
  // invoice of one kind with no line placed needs none: everything goes to its one place.
  const receive = useChange(
    async (
      input: {
        id: string;
        locationId: string;
        commitKey: string;
        lines?: { id: string; locationId: string }[];
      },
      key: string,
    ) => {
      if (input.lines) {
        await send.patchInvoice({ id: input.id, patch: { lines: input.lines } }, `${key}-place`);
      }
      return send.commitInvoice(input, key);
    },
    {
      onSuccess: (result) => {
        notify({
          title: `Received ${result.lotsCreated} ${result.lotsCreated === 1 ? "lot" : "lots"}`,
          // Kept as data, so it can be pressed from the bell or the Notifications page later.
          action: { kind: "undo_receipt", invoiceId: invoice.id },
          link: { kind: "invoice", invoiceId: invoice.id },
        });
        navigate({ to: "/invoices/$invoiceId", params: { invoiceId: invoice.id } });
      },
    },
  );
  const startReceiving = () => {
    const lines = stockLines.map((l) => ({
      id: l.id,
      locationId: custom ? (placeOf(l) as string) : choiceOf(kindOf(l)),
    }));
    const placeLines = !custom && (groups.length > 1 || stockLines.some((l) => l.locationId));
    receive.mutate({
      id: invoice.id,
      // With each line placed, this is only where a line with none would go, and none has.
      locationId: (lines[0] as { locationId: string }).locationId,
      commitKey: invoice.commitKey,
      ...(placeLines ? { lines } : {}),
    });
  };

  // What the brewer counted against what the supplier billed. Scott takes a shortfall up with the
  // seller and the results vary (Tyler, 2026-09-22), so what he needs is the difference in front of
  // him before he commits, named line by line.
  const miscounted = stockLines
    .filter((l) => l.receivedUnits !== null && !dec(l.receivedUnits).eq(dec(l.billedUnits)))
    .map((l) => {
      const received = l.receivedUnits as string;
      const difference = dec(received).minus(dec(l.billedUnits));
      return {
        id: l.id,
        name: l.itemName ?? l.description,
        billed: l.billedUnits,
        received,
        short: difference.lt(ZERO),
        by: difference.abs().toString(),
      };
    });

  return (
    <section className="section">
      <div className="section-header" style={{ cursor: "default" }}>
        <h2>About to receive</h2>
      </div>
      <div className="section-body stack">
        <dl className="receive-summary">
          <div>
            <dt>Lines delivering stock</dt>
            <dd className="num">{stockLines.length}</dd>
          </div>
          <div>
            <dt>Lots to create</dt>
            <dd className="num">{lots}</dd>
          </div>
          <div>
            <dt>Goods</dt>
            <dd className="num">{money(invoice.computed.goods)}</dd>
          </div>
          <div>
            <dt>
              Freight, fees and taxes
              <span className="muted">
                {" "}
                spread{" "}
                {METHODS.find((m) => m.value === invoice.allocationMethod)?.label.toLowerCase()}
              </span>
            </dt>
            <dd className="num">{money(invoice.computed.charges)}</dd>
          </div>
        </dl>

        {unpacked.length > 0 ? (
          <div className="notice warn" role="status">
            <TriangleAlert size={16} />
            <div>
              <strong>
                {unpacked.length === 1
                  ? "One line has no pack size."
                  : `${unpacked.length} lines have no pack size.`}
              </strong>
              <ul>
                {unpacked.map((l) => (
                  <li key={l.id}>
                    {l.itemName ?? l.description}: {packProblem(l, dimensionOf(l.itemId))}{" "}
                    <GivePack itemId={l.itemId} />
                  </li>
                ))}
              </ul>
              <span className="muted">
                Receiving counts each line in packs, so it needs to know how much is in one.
              </span>
            </div>
          </div>
        ) : null}

        {unmatched.length > 0 ? (
          <div className="notice warn" role="status">
            <TriangleAlert size={16} />
            <div>
              {unmatched.length} {unmatched.length === 1 ? "line has" : "lines have"} no item yet.
              Go back to the first step and match {unmatched.length === 1 ? "it" : "them"}.
            </div>
          </div>
        ) : null}

        {miscounted.length > 0 ? (
          <div className="notice warn" role="status">
            <TriangleAlert size={16} />
            <div>
              <strong>
                {miscounted.length === 1
                  ? "One line did not arrive as billed."
                  : `${miscounted.length} lines did not arrive as billed.`}
              </strong>
              <ul>
                {miscounted.map((line) => (
                  <li key={line.id}>
                    {line.name}: {line.billed} billed, {line.received} arrived —{" "}
                    {line.short ? `${line.by} short` : `${line.by} over`}
                  </li>
                ))}
              </ul>
              <span className="muted">
                Stock goes in at what arrived. The invoice keeps what it billed, so the difference
                stays on it if you take it up with the supplier.
              </span>
            </div>
          </div>
        ) : null}

        <div className="place-groups">
          {groups.map((g) => (
            <div
              key={g.kind}
              className={`place-group${g.category ? "" : " mixed"}`}
              style={g.category ? catStyle(g.category) : undefined}
            >
              <div className="place-group-head">
                <MapPin className="place-pin" size={18} aria-hidden />
                <span className="place-group-name">
                  <strong>{g.label}</strong>
                  <span className="muted">
                    {" · "}
                    {g.lines.length} {plural("line", String(g.lines.length))}
                  </span>
                </span>
                {custom ? null : (
                  <PlaceSelect
                    label={`Location for ${g.label}`}
                    value={choiceOf(g.kind)}
                    open={open}
                    onChange={(id) => choose(g.kind, id)}
                  />
                )}
              </div>
              {custom ? (
                <div className="line-places">
                  {g.lines.map((l) => {
                    const name = l.itemName ?? l.description;
                    return (
                      <div className="line-place" key={l.id}>
                        <span className="line-place-name">
                          {name}
                          <span className="muted">
                            {" · "}
                            {l.receivedUnits ?? l.billedUnits}
                            {l.pack?.known ? ` × ${l.pack.label}` : ""}
                          </span>
                        </span>
                        <PlaceSelect
                          label={`Location for ${name}`}
                          value={placeOf(l) ?? ""}
                          open={open}
                          disabled={place.isPending}
                          onChange={(id) => putLines([{ id: l.id, locationId: id || null }])}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="place-group-items">
                  {g.lines.map((l) => l.itemName ?? l.description).join(", ")}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="row">
          <button
            type="button"
            className="link-button"
            disabled={place.isPending}
            onClick={() => turnCustom(!custom)}
          >
            <SlidersHorizontal size={14} aria-hidden />{" "}
            {custom ? "Use one location for each kind" : "Customize location per item"}
          </button>
        </div>
        {custom ? (
          <span className="hint">
            Each item starts where its kind was going. Going back to one location for each kind
            clears the items' own.
          </span>
        ) : null}

        <ErrorNote error={place.error} />

        <ErrorNote error={receive.error} />
        <div className="row">
          {placeChosen ? null : (
            <span className="muted">Choose a location for each box in red.</span>
          )}
          <span className="spacer" />
          <button
            type="button"
            className="button primary"
            disabled={
              receive.isPending ||
              place.isPending ||
              unmatched.length > 0 ||
              unpacked.length > 0 ||
              !placeChosen ||
              stockLines.length === 0
            }
            onClick={startReceiving}
          >
            {receive.isPending ? "Receiving" : "Receive this invoice"}
          </button>
        </div>
        <p className="muted">
          All of it or none of it, and only once: a second press cannot receive it twice.
        </p>
      </div>
    </section>
  );
}
